import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react'
import { authService, UserInfo } from '@/services/auth/authService'
import { loadTenantEntryConfig } from '@/services/tenantEntryConfig'
import { authLogger } from '@/utils/logger'

interface AuthContextType {
  isAuthenticated: boolean
  user: UserInfo | null
  isLoading: boolean
  isImpersonating: boolean
  /** Start an interactive authorization-code login (redirects to the ABP login page). */
  beginLogin: (tenantId?: string, returnUrl?: string) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  /** Re-read identity/impersonation from authService after an in-app transition. */
  refreshAuthState: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Routes that own their own auth handling — the boot logic must not fight them.
const CALLBACK_PATHS = ['/auth/callback', '/auth/logged-out']

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<UserInfo | null>(null)
  const [isImpersonating, setIsImpersonating] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const didInit = useRef(false)

  const refreshAuthState = useCallback(() => {
    setIsAuthenticated(authService.isAuthenticated())
    setUser(authService.getCurrentUser())
    setIsImpersonating(authService.isImpersonating())
  }, [])

  const initialize = useCallback(async () => {
    const path = window.location.pathname

    // The callback/logged-out routes drive their own flow (see AuthCallback / LoggedOut).
    if (CALLBACK_PATHS.some((p) => path.startsWith(p))) {
      setIsLoading(false)
      return
    }

    try {
      // In-memory user only survives a client-side navigation; on a fresh load it's null.
      const existing = await authService.loadUser()
      if (existing) {
        await authService.fetchGrantedPermissions()
        // A client-side navigation keeps the in-memory session; the bounds still have to
        // be known on this load, or the countdown has nothing to count towards.
        await authService.loadSessionPolicy()
        authService.startAutoRefresh()
        refreshAuthState()
        setIsLoading(false)
        return
      }

      // The interactive login page must not auto-challenge (it lets the user pick a
      // tenant and hand off to the ABP login page); everywhere else, attempt exactly
      // one silent top-level prompt=none round per load (D7 auto-challenge policy).
      if (path === '/login') {
        setIsLoading(false)
        return
      }

      // The automatic round is an authorize request like any other: it must carry the
      // `__tenant` hint in a pre-D18 installation and must not carry one on a tenant
      // subdomain. Both need the installation's answer, so it is fetched before the
      // first round rather than after it.
      await loadTenantEntryConfig()

      const redirecting = await authService.beginSilentSignin(window.location.href)
      if (redirecting) {
        // Navigating away to the auth host — keep the loading state until we return.
        return
      }

      // Silent attempt already spent this load → unauthenticated; ProtectedRoute → /login.
      setIsLoading(false)
    } catch (error) {
      authLogger.error('Auth initialization failed', error instanceof Error ? error : undefined)
      setIsAuthenticated(false)
      setUser(null)
      setIsLoading(false)
    }
  }, [refreshAuthState])

  useEffect(() => {
    if (didInit.current) return
    didInit.current = true
    void initialize()
  }, [initialize])

  const beginLogin = useCallback(async (tenantId?: string, returnUrl?: string) => {
    setIsLoading(true)
    await authService.beginInteractiveLogin(tenantId, returnUrl)
    // Redirects away; control does not return here on success.
  }, [])

  const logout = useCallback(async () => {
    authLogger.debug('Logout process started')
    try {
      setIsLoading(true)
      await authService.logout()
      // Redirects to end_session; control does not return here on success.
    } catch (error) {
      authLogger.error('Logout error', error instanceof Error ? error : undefined)
      setIsAuthenticated(false)
      setUser(null)
      setIsImpersonating(false)
      setIsLoading(false)
    }
  }, [])

  const refreshUser = useCallback(async () => {
    try {
      refreshAuthState()
    } catch (error) {
      authLogger.error('Failed to refresh user info', error instanceof Error ? error : undefined)
      await logout()
    }
  }, [refreshAuthState, logout])

  const value: AuthContextType = {
    isAuthenticated,
    user,
    isLoading,
    isImpersonating,
    beginLogin,
    logout,
    refreshUser,
    refreshAuthState,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
